import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../core/auth.service';
import { PageMetaService } from '../../../reminders/core/services/page-meta.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './login.html',
  styleUrl: '../auth-page.scss',
})
export class Login implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly pageMeta = inject(PageMetaService);

  readonly loading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly passwordResetSuccess = signal(false);
  readonly passwordChangedSuccess = signal(false);
  readonly currentYear = new Date().getFullYear();

  readonly form = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });

  ngOnInit(): void {
    this.pageMeta.set(
      'Log in - Every Rupee Spent',
      'Log in to everyrupeespent.com to manage your reminders, bills, and more.',
    );
    this.passwordResetSuccess.set(this.route.snapshot.queryParamMap.get('passwordReset') === 'success');
    this.passwordChangedSuccess.set(this.route.snapshot.queryParamMap.get('passwordChanged') === 'success');
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    const { email, password } = this.form.getRawValue();

    this.authService.login(email, password).subscribe({
      next: () => {
        const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
        this.authService.loadCurrentUser().subscribe(() => this.router.navigateByUrl(returnUrl ?? '/'));
      },
      error: (err: unknown) => {
        this.loading.set(false);
        this.errorMessage.set(this.loginErrorMessage(err));
      },
    });
  }

  private loginErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 429) {
        return 'Too many login attempts. Please wait a minute and try again.';
      }
      if (err.status === 0) {
        return 'Could not reach the server. Check your connection and try again.';
      }
    }
    return 'Invalid email or password.';
  }
}
